import {
  Autocomplete,
  Button,
  Checkbox,
  TextField,
  Box,
  Typography,
  CircularProgress,
  FormGroup,
  FormControlLabel,
  FormLabel,
  FormControl,
} from '@mui/material'
import axios from 'axios'
import PageAppBar from 'components/PageAppBar'
import { base_url } from 'env'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { PermissionsModel, ProfileModel, UserModel } from 'types'
import { toast } from 'react-hot-toast'

const initialPermissions: PermissionsModel = {
  doActivities: false,
  editActivityHistory: false,
  consultActivityHistory: false,
  doTest: false,
  editTestHistory: false,
  consultTestHistory: false,
  reEnableTests: false,
  editPrizes: false,
  addPrizes: false,
  consultPrizes: false,
}

export default function LicenseCreatePage() {
  const navigate = useNavigate()
  // --- REFACTORED: State is now cleaner and more direct ---
  const [creatorLogin, setCreatorLogin] = useState<string | null>(null)
  const [invitedUserLogin, setInvitedUserLogin] = useState<string | null>(null)
  const [isMedical, setIsMedical] = useState(false)
  const [selectedProfiles, setSelectedProfiles] = useState<ProfileModel[]>([])
  const [permissions, setPermissions] = useState(initialPermissions)

  // State for populating dropdowns
  const [allUsers, setAllUsers] = useState<UserModel[]>([])
  const [allProfiles, setAllProfiles] = useState<ProfileModel[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    setIsLoading(true)
    Promise.all([
      axios.get(base_url + '/user/'),
      axios.get(base_url + '/profile/'),
    ])
      .then(([usersRes, profilesRes]) => {
        setAllUsers(usersRes.data)
        setAllProfiles(profilesRes.data)
      })
      .catch((error) => {
        console.error('Failed to load data for license creation', error)
        navigate('/licenses') // Redirect if essential data fails to load
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [navigate])

  const handlePermissionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPermissions({
      ...permissions,
      [e.target.name]: e.target.checked,
    })
  }

  const handleSubmit = () => {
    if (selectedProfiles.length === 0) {
      toast.error('Veuillez sélectionner au moins un profil à lier.')
      return
    }
    setIsSaving(true)

    // --- REFACTORED: Dynamically build payload, omitting null/empty keys ---
    const payload: { [key: string]: any } = {
      isMedical: isMedical,
      permissions: permissions,
      profiles: selectedProfiles.map((p) => p.id),
    }
    if (creatorLogin) payload.creatorLogin = creatorLogin
    if (invitedUserLogin) payload.invitedUserLogin = invitedUserLogin

    axios
      .post(base_url + '/license/', payload)
      .then((response) => {
        toast.success('Licence créée avec succès !')
        navigate(`/license/${response.data.licenseKey}`)
      })
      .catch((error) => {
        console.error('License creation failed:', error)
        // Global handler will show the error toast
      })
      .finally(() => {
        setIsSaving(false)
      })
  }

  if (isLoading) {
    return (
      <PageAppBar pageName="Création de Licence">
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </PageAppBar>
    )
  }

  return (
    <PageAppBar pageName="Créer une Licence">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          alignItems: 'center',
          p: 3,
          maxWidth: '800px',
          margin: 'auto',
        }}
      >
        <Typography variant="h5">Nouvelle Licence</Typography>

        <Autocomplete
          fullWidth
          options={allUsers}
          getOptionLabel={(option) => option.login}
          onChange={(event, option) => setCreatorLogin(option?.login || null)}
          renderInput={(params) => (
            <TextField {...params} label="Compte créateur" />
          )}
        />
        <Autocomplete
          fullWidth
          options={allUsers}
          getOptionLabel={(option) => option.login}
          onChange={(event, option) =>
            setInvitedUserLogin(option?.login || null)
          }
          renderInput={(params) => (
            <TextField {...params} label="Compte invité (Optionnel)" />
          )}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={isMedical}
              onChange={(e) => setIsMedical(e.target.checked)}
            />
          }
          label="Licence Médicale"
          sx={{ alignSelf: 'flex-start' }}
        />

        <Autocomplete
          multiple
          fullWidth
          options={allProfiles}
          getOptionLabel={(option) =>
            `${option.firstName} ${option.lastName || ''}`
          }
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onChange={(event, value) => setSelectedProfiles(value)}
          renderInput={(params) => (
            <TextField {...params} label="Profils à lier *" />
          )}
        />

        <FormControl component="fieldset" fullWidth sx={{ mt: 2 }}>
          <FormLabel component="legend">Permissions</FormLabel>
          <FormGroup row>
            <FormControlLabel
              control={
                <Checkbox
                  checked={permissions.doActivities}
                  onChange={handlePermissionChange}
                  name="doActivities"
                />
              }
              label="Faire activités"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={permissions.consultActivityHistory}
                  onChange={handlePermissionChange}
                  name="consultActivityHistory"
                />
              }
              label="Consulter historique"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={permissions.editActivityHistory}
                  onChange={handlePermissionChange}
                  name="editActivityHistory"
                />
              }
              label="Éditer historique"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={permissions.doTest}
                  onChange={handlePermissionChange}
                  name="doTest"
                />
              }
              label="Faire tests"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={permissions.consultTestHistory}
                  onChange={handlePermissionChange}
                  name="consultTestHistory"
                />
              }
              label="Consulter tests"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={permissions.editTestHistory}
                  onChange={handlePermissionChange}
                  name="editTestHistory"
                />
              }
              label="Éditer tests"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={permissions.reEnableTests}
                  onChange={handlePermissionChange}
                  name="reEnableTests"
                />
              }
              label="Activer tests"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={permissions.addPrizes}
                  onChange={handlePermissionChange}
                  name="addPrizes"
                />
              }
              label="Ajouter récompenses"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={permissions.consultPrizes}
                  onChange={handlePermissionChange}
                  name="consultPrizes"
                />
              }
              label="Consulter récompenses"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={permissions.editPrizes}
                  onChange={handlePermissionChange}
                  name="editPrizes"
                />
              }
              label="Éditer récompenses"
            />
          </FormGroup>
        </FormControl>

        <Button
          variant="contained"
          color="success"
          onClick={handleSubmit}
          disabled={isSaving}
          sx={{ mt: 2, minWidth: '150px' }}
        >
          {isSaving ? <CircularProgress size={24} /> : 'Créer'}
        </Button>
      </Box>
    </PageAppBar>
  )
}
